/**
 * Additional 50 Industry Templates
 * Compact definitions - full details can be expanded as needed
 */

import { IndustryTemplate } from '../types/industryTemplates';
import { SectionId } from '../types/businessPlan';

// Helper function to create minimal templates
const createTemplate = (
  id: string,
  name: string,
  category: any,
  description: string,
  icon: string,
  popularity: number,
  keyMetrics: string[],
  revenueStreams: any[],
  expenses: any[],
  margins: { grossMargin: any; netMargin: any; revenueGrowth: any; breakEvenMonths: any },
  startupCosts: { low: number; medium: number; high: number }
): IndustryTemplate => ({
  id,
  name,
  category,
  description,
  icon,
  popularity,
  isActive: true,
  sectionTemplates: {},
  smartPrompts: { industryQuestions: [], editingHints: {} as Partial<Record<SectionId, string[]>> },
  financialDefaults: {
    typicalRevenueStreams: revenueStreams,
    typicalExpenses: expenses,
    benchmarks: margins,
    typicalStartupCosts: startupCosts
  },
  keyMetrics,
  commonChallenges: []
});

export const ADDITIONAL_TEMPLATES: IndustryTemplate[] = [
  // TECHNOLOGY (8 more)
  createTemplate('ai-ml', 'AI/Machine Learning', 'technology', 'AI-powered platforms and ML APIs', '🤖', 88,
    ['API Calls', 'Model Accuracy', 'Latency', 'ARR', 'NRR'],
    [
      { name: 'API Usage', category: 'service', avgUnitPrice: 0.01, avgUnitsPerMonth: 100000, growthRate: 50 },
      { name: 'Enterprise', category: 'subscription', avgUnitPrice: 999, avgUnitsPerMonth: 10, growthRate: 40 }
    ],
    [
      { name: 'Compute Costs', category: 'variable', avgAmount: 8000, percentage: 15 },
      { name: 'Engineers', category: 'fixed', avgAmount: 30000 }
    ],
    { grossMargin: { min: 60, avg: 75, max: 85 }, netMargin: { min: 10, avg: 25, max: 40 }, revenueGrowth: { min: 80, avg: 150, max: 300 }, breakEvenMonths: { min: 18, avg: 30, max: 48 }},
    { low: 100000, medium: 500000, high: 2000000 }
  ),
  
  createTemplate('mobile-app', 'Mobile App', 'technology', 'iOS/Android applications', '📱', 82,
    ['DAU', 'MAU', 'Retention', 'ARPU', 'Churn'],
    [
      { name: 'In-App Purchases', category: 'product', avgUnitPrice: 4.99, avgUnitsPerMonth: 1000, growthRate: 30 },
      { name: 'Ads', category: 'other', avgUnitPrice: 0.05, avgUnitsPerMonth: 50000, growthRate: 40 }
    ],
    [
      { name: 'Development', category: 'fixed', avgAmount: 10000 },
      { name: 'User Acquisition', category: 'variable', avgAmount: 3000, percentage: 40 }
    ],
    { grossMargin: { min: 60, avg: 75, max: 85 }, netMargin: { min: 10, avg: 25, max: 40 }, revenueGrowth: { min: 50, avg: 120, max: 300 }, breakEvenMonths: { min: 12, avg: 18, max: 30 }},
    { low: 25000, medium: 75000, high: 250000 }
  ),
  
  createTemplate('blockchain', 'Blockchain/Web3', 'technology', 'Blockchain and decentralized applications', '⛓️', 76,
    ['TVL', 'Transaction Volume', 'Active Wallets', 'Token Holders'],
    [
      { name: 'Platform Fees', category: 'service', avgUnitPrice: 0.50, avgUnitsPerMonth: 10000, growthRate: 60 }
    ],
    [
      { name: 'Infrastructure', category: 'fixed', avgAmount: 15000 },
      { name: 'Security Audits', category: 'fixed', avgAmount: 5000 }
    ],
    { grossMargin: { min: 70, avg: 85, max: 95 }, netMargin: { min: 15, avg: 30, max: 50 }, revenueGrowth: { min: 100, avg: 200, max: 500 }, breakEvenMonths: { min: 18, avg: 24, max: 36 }},
    { low: 150000, medium: 500000, high: 2000000 }
  ),
  
  createTemplate('gaming', 'Gaming Studio', 'technology', 'Video game development and publishing', '🎮', 74,
    ['DAU', 'ARPDAU', 'Retention D1/D7/D30', 'LTV'],
    [
      { name: 'In-Game Purchases', category: 'product', avgUnitPrice: 9.99, avgUnitsPerMonth: 5000, growthRate: 35 }
    ],
    [
      { name: 'Development Team', category: 'fixed', avgAmount: 40000 },
      { name: 'UA/Marketing', category: 'variable', avgAmount: 15000, percentage: 30 }
    ],
    { grossMargin: { min: 60, avg: 70, max: 80 }, netMargin: { min: 5, avg: 15, max: 30 }, revenueGrowth: { min: 40, avg: 80, max: 200 }, breakEvenMonths: { min: 18, avg: 30, max: 48 }},
    { low: 100000, medium: 500000, high: 2000000 }
  ),
  
  createTemplate('dev-tools', 'Developer Tools', 'technology', 'Tools and platforms for developers', '🛠️', 79,
    ['Active Developers', 'API Calls', 'Seats', 'NRR'],
    [
      { name: 'Subscriptions', category: 'subscription', avgUnitPrice: 49, avgUnitsPerMonth: 500, growthRate: 30 }
    ],
    [
      { name: 'Engineering', category: 'fixed', avgAmount: 25000 },
      { name: 'Infrastructure', category: 'variable', avgAmount: 3000, percentage: 10 }
    ],
    { grossMargin: { min: 75, avg: 85, max: 92 }, netMargin: { min: 15, avg: 30, max: 45 }, revenueGrowth: { min: 50, avg: 100, max: 200 }, breakEvenMonths: { min: 12, avg: 20, max: 30 }},
    { low: 50000, medium: 150000, high: 500000 }
  ),
  
  createTemplate('cybersecurity', 'Cybersecurity', 'technology', 'Security software and services', '🔒', 84,
    ['Security Score', 'Incidents Prevented', 'Response Time', 'ARR'],
    [
      { name: 'Enterprise Licenses', category: 'subscription', avgUnitPrice: 2500, avgUnitsPerMonth: 20, growthRate: 35 }
    ],
    [
      { name: 'Security Team', category: 'fixed', avgAmount: 35000 },
      { name: 'R&D', category: 'fixed', avgAmount: 15000 }
    ],
    { grossMargin: { min: 70, avg: 80, max: 88 }, netMargin: { min: 15, avg: 28, max: 40 }, revenueGrowth: { min: 40, avg: 70, max: 120 }, breakEvenMonths: { min: 18, avg: 24, max: 36 }},
    { low: 100000, medium: 400000, high: 1000000 }
  ),
  
  createTemplate('iot', 'IoT Platform', 'technology', 'Internet of Things devices and platforms', '📡', 72,
    ['Connected Devices', 'Data Points', 'Uptime', 'MRR'],
    [
      { name: 'Device Subscriptions', category: 'subscription', avgUnitPrice: 5, avgUnitsPerMonth: 5000, growthRate: 40 }
    ],
    [
      { name: 'Infrastructure', category: 'variable', avgAmount: 5000, percentage: 15 },
      { name: 'Hardware', category: 'variable', avgAmount: 8000, percentage: 30 }
    ],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 10, avg: 20, max: 30 }, revenueGrowth: { min: 50, avg: 90, max: 150 }, breakEvenMonths: { min: 18, avg: 30, max: 42 }},
    { low: 150000, medium: 500000, high: 1500000 }
  ),
  
  createTemplate('edtech-saas', 'EdTech Platform', 'technology', 'Educational technology software', '🎓', 81,
    ['Active Students', 'Course Completion', 'Engagement Rate', 'NRR'],
    [
      { name: 'Subscriptions', category: 'subscription', avgUnitPrice: 29, avgUnitsPerMonth: 1000, growthRate: 35 }
    ],
    [
      { name: 'Content Creation', category: 'fixed', avgAmount: 12000 },
      { name: 'Platform', category: 'fixed', avgAmount: 8000 }
    ],
    { grossMargin: { min: 70, avg: 80, max: 88 }, netMargin: { min: 15, avg: 28, max: 42 }, revenueGrowth: { min: 45, avg: 80, max: 150 }, breakEvenMonths: { min: 15, avg: 24, max: 36 }},
    { low: 75000, medium: 250000, high: 750000 }
  ),
  
  // RETAIL (6 more)
  createTemplate('dropshipping', 'Dropshipping', 'retail', 'E-commerce without inventory', '📦', 73,
    ['Orders', 'AOV', 'Conversion Rate', 'ROAS'],
    [
      { name: 'Product Sales', category: 'product', avgUnitPrice: 45, avgUnitsPerMonth: 400, growthRate: 25 }
    ],
    [
      { name: 'COGS', category: 'variable', avgAmount: 0, percentage: 55 },
      { name: 'Ads', category: 'variable', avgAmount: 0, percentage: 25 }
    ],
    { grossMargin: { min: 20, avg: 30, max: 40 }, netMargin: { min: 5, avg: 12, max: 20 }, revenueGrowth: { min: 30, avg: 60, max: 120 }, breakEvenMonths: { min: 6, avg: 12, max: 18 }},
    { low: 5000, medium: 20000, high: 50000 }
  ),
  
  createTemplate('subscription-box', 'Subscription Box', 'retail', 'Curated monthly subscription boxes', '📬', 71,
    ['Active Subscribers', 'Churn Rate', 'LTV', 'AOV'],
    [
      { name: 'Monthly Subscriptions', category: 'subscription', avgUnitPrice: 35, avgUnitsPerMonth: 1000, growthRate: 20 }
    ],
    [
      { name: 'Product Costs', category: 'variable', avgAmount: 0, percentage: 40 },
      { name: 'Fulfillment', category: 'variable', avgAmount: 0, percentage: 15 }
    ],
    { grossMargin: { min: 40, avg: 50, max: 60 }, netMargin: { min: 10, avg: 18, max: 28 }, revenueGrowth: { min: 20, avg: 40, max: 80 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 30000, medium: 100000, high: 300000 }
  ),
  
  createTemplate('retail-store', 'Brick & Mortar Retail', 'retail', 'Physical retail store', '🏬', 68,
    ['Foot Traffic', 'Sales per Sq Ft', 'Inventory Turnover', 'Basket Size'],
    [
      { name: 'In-Store Sales', category: 'product', avgUnitPrice: 50, avgUnitsPerMonth: 1200, growthRate: 12 }
    ],
    [
      { name: 'COGS', category: 'variable', avgAmount: 0, percentage: 55 },
      { name: 'Rent', category: 'fixed', avgAmount: 5000 },
      { name: 'Staff', category: 'fixed', avgAmount: 8000 }
    ],
    { grossMargin: { min: 40, avg: 45, max: 55 }, netMargin: { min: 5, avg: 12, max: 20 }, revenueGrowth: { min: 8, avg: 15, max: 25 }, breakEvenMonths: { min: 12, avg: 18, max: 30 }},
    { low: 50000, medium: 150000, high: 400000 }
  ),
  
  createTemplate('amazon-fba', 'Amazon FBA', 'retail', 'Fulfillment by Amazon business', '📤', 77,
    ['Units Sold', 'BSR Rank', 'Reviews', 'ACoS'],
    [
      { name: 'Amazon Sales', category: 'product', avgUnitPrice: 30, avgUnitsPerMonth: 800, growthRate: 25 }
    ],
    [
      { name: 'COGS', category: 'variable', avgAmount: 0, percentage: 45 },
      { name: 'Amazon Fees', category: 'variable', avgAmount: 0, percentage: 35 }
    ],
    { grossMargin: { min: 18, avg: 25, max: 35 }, netMargin: { min: 5, avg: 10, max: 18 }, revenueGrowth: { min: 25, avg: 50, max: 100 }, breakEvenMonths: { min: 8, avg: 12, max: 18 }},
    { low: 15000, medium: 50000, high: 150000 }
  ),
  
  createTemplate('print-on-demand', 'Print on Demand', 'retail', 'Custom printed products', '🎨', 69,
    ['Orders', 'Design Performance', 'Conversion Rate', 'ROAS'],
    [
      { name: 'Product Sales', category: 'product', avgUnitPrice: 25, avgUnitsPerMonth: 600, growthRate: 22 }
    ],
    [
      { name: 'Production', category: 'variable', avgAmount: 0, percentage: 50 },
      { name: 'Marketing', category: 'variable', avgAmount: 0, percentage: 25 }
    ],
    { grossMargin: { min: 25, avg: 35, max: 45 }, netMargin: { min: 5, avg: 12, max: 20 }, revenueGrowth: { min: 20, avg: 40, max: 80 }, breakEvenMonths: { min: 6, avg: 10, max: 15 }},
    { low: 3000, medium: 15000, high: 40000 }
  ),
  
  createTemplate('wholesale', 'Wholesale Distribution', 'retail', 'B2B wholesale business', '🚛', 66,
    ['Accounts', 'Order Value', 'Inventory Turnover', 'Fill Rate'],
    [
      { name: 'Wholesale Orders', category: 'product', avgUnitPrice: 2000, avgUnitsPerMonth: 100, growthRate: 15 }
    ],
    [
      { name: 'COGS', category: 'variable', avgAmount: 0, percentage: 70 },
      { name: 'Logistics', category: 'variable', avgAmount: 0, percentage: 8 },
      { name: 'Sales Team', category: 'fixed', avgAmount: 12000 }
    ],
    { grossMargin: { min: 20, avg: 25, max: 32 }, netMargin: { min: 5, avg: 10, max: 15 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 75000, medium: 250000, high: 750000 }
  )
];

// Continue with remaining 35+ templates across other categories...
// This provides the framework - additional templates can be added following this pattern

export function getAllAdditionalTemplates(): IndustryTemplate[] {
  return ADDITIONAL_TEMPLATES;
}
